import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatChipInputEvent} from '@angular/material/chips';
import {Subject, takeUntil} from 'rxjs';
import {MaterialModule} from '../../material.module';
import {
  CmsContentType,
  ConnectionInfo,
  CmsPageSummary,
  CmsLanguageVariation,
  TranslationJob,
  TranslationJobRequest,
    TranslationSyncConfig,
    TranslationSyncMode,
    TranslationSyncResult,
  TranslationSettings,
  TranslationsApiService,
} from '../../services/translations/translations-api.service';
import {ConfirmDialogService} from '../../services/layout/dialog.service';
import {ToastService} from '../../services/layout/toast.service';
import {OPENAI_LUNA_MODEL} from '../../models/models.model';

type LanguageOption = {code: string; label: string};

const LANGUAGE_OPTIONS: LanguageOption[] = [
  {code: 'en', label: 'English'},
  {code: 'en-us', label: 'English (United States)'},
  {code: 'en-gb', label: 'English (United Kingdom)'},
  {code: 'en-au', label: 'English (Australia)'},
  {code: 'en-ca', label: 'English (Canada)'},
  {code: 'no', label: 'Norwegian'},
  {code: 'nb', label: 'Norwegian Bokmal'},
  {code: 'nn', label: 'Norwegian Nynorsk'},
  {code: 'da', label: 'Danish'},
  {code: 'sv', label: 'Swedish'},
  {code: 'fi', label: 'Finnish'},
  {code: 'is', label: 'Icelandic'},
  {code: 'de', label: 'German'},
  {code: 'de-at', label: 'German (Austria)'},
  {code: 'de-ch', label: 'German (Switzerland)'},
  {code: 'fr', label: 'French'},
  {code: 'fr-ca', label: 'French (Canada)'},
  {code: 'es', label: 'Spanish'},
  {code: 'es-es', label: 'Spanish (Spain)'},
  {code: 'es-419', label: 'Spanish (Latin America)'},
  {code: 'it', label: 'Italian'},
  {code: 'pt', label: 'Portuguese'},
  {code: 'pt-br', label: 'Portuguese (Brazil)'},
  {code: 'nl', label: 'Dutch'},
  {code: 'pl', label: 'Polish'},
  {code: 'cs', label: 'Czech'},
  {code: 'sk', label: 'Slovak'},
  {code: 'sl', label: 'Slovenian'},
  {code: 'hu', label: 'Hungarian'},
  {code: 'ro', label: 'Romanian'},
  {code: 'bg', label: 'Bulgarian'},
  {code: 'hr', label: 'Croatian'},
  {code: 'sr', label: 'Serbian'},
  {code: 'uk', label: 'Ukrainian'},
  {code: 'ru', label: 'Russian'},
  {code: 'el', label: 'Greek'},
  {code: 'tr', label: 'Turkish'},
  {code: 'he', label: 'Hebrew'},
  {code: 'ar', label: 'Arabic'},
  {code: 'fa', label: 'Persian'},
  {code: 'hi', label: 'Hindi'},
  {code: 'bn', label: 'Bengali'},
  {code: 'ta', label: 'Tamil'},
  {code: 'te', label: 'Telugu'},
  {code: 'ur', label: 'Urdu'},
  {code: 'th', label: 'Thai'},
  {code: 'vi', label: 'Vietnamese'},
  {code: 'id', label: 'Indonesian'},
  {code: 'ms', label: 'Malay'},
  {code: 'tl', label: 'Tagalog'},
  {code: 'sw', label: 'Swahili'},
  {code: 'ja', label: 'Japanese'},
  {code: 'ko', label: 'Korean'},
  {code: 'zh', label: 'Chinese'},
  {code: 'zh-cn', label: 'Chinese (Simplified)'},
  {code: 'zh-hans', label: 'Chinese (Simplified, Hans)'},
  {code: 'zh-hant', label: 'Chinese (Traditional, Hant)'},
  {code: 'zh-hk', label: 'Chinese (Hong Kong)'},
  {code: 'zh-tw', label: 'Chinese (Taiwan)'},
  {code: 'et', label: 'Estonian'},
  {code: 'lv', label: 'Latvian'},
  {code: 'lt', label: 'Lithuanian'},
  {code: 'ga', label: 'Irish'},
  {code: 'mt', label: 'Maltese'},
  {code: 'ca', label: 'Catalan'},
  {code: 'eu', label: 'Basque'},
  {code: 'gl', label: 'Galician'},
];

const DEFAULT_SYNC_CONFIG: TranslationSyncConfig = {
    mode: 'off',
    includeIds: [],
    excludeIds: [],
};

@Component({
  selector: 'app-translations-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './translations-admin.component.html',
  styleUrls: ['./translations-admin.component.scss'],
})
export class TranslationsAdminComponent implements OnInit, OnDestroy {
  private readonly api = inject(TranslationsApiService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);
  private readonly destroy$ = new Subject<void>();

  connections: ConnectionInfo[] = [];
  selectedConnectionId = '';

  contentType: CmsContentType = 'SitePage';
  pages: CmsPageSummary[] = [];
  pagesNext?: string | null;
  pagesLoading = false;
  pagesLoadError = '';

  targetLanguage = '';
  jobInstructions = '';

  jobs: TranslationJob[] = [];
  jobsLoading = false;
  jobsLoadError = '';
  selectedTabIndex = 0;

  settings: TranslationSettings = {
    clientId: '',
    defaultInstructions:
      'Translate the provided content into the target language with correct grammar and tone. ' +
      'Preserve meaning, formatting, HTML tags, HubL tokens, variables, and URLs. ' +
      'Do not translate product names or brand terms. If text should remain unchanged, return it verbatim.',
    defaultModel: OPENAI_LUNA_MODEL,
    formPolicy: 'auto',
    ctaPolicy: 'auto',
      autoTranslateLanguages: [],
      syncConnectionId: '',
      syncSitePages: {...DEFAULT_SYNC_CONFIG},
      syncLandingPages: {...DEFAULT_SYNC_CONFIG},
      syncBlogPosts: {...DEFAULT_SYNC_CONFIG},
  };

  readonly policyOptions = [
    {value: 'auto', label: 'Auto (best effort)'},
    {value: 'keep', label: 'Keep as-is'},
  ];
    readonly syncModeOptions: Array<{ value: TranslationSyncMode; label: string }> = [
        {value: 'off', label: 'Off'},
        {value: 'include', label: 'Only selected pages'},
        {value: 'exclude', label: 'All pages except excluded'},
    ];
  readonly languageOptions = LANGUAGE_OPTIONS;
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    autoLanguageInput = '';

  ngOnInit(): void {
    this.refreshConnections();
    this.loadSettings();
    this.loadJobs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refreshConnections(): void {
    this.api
      .listConnections()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (connections) => {
          this.connections = connections ?? [];
          if (!this.selectedConnectionId && this.connections.length) {
            this.selectedConnectionId = this.connections[0].id;
          }
        },
        error: (err) => {
          console.error('Failed to load HubSpot connections', err);
          this.toastService.showError('Could not load HubSpot connections', 3000);
        },
      });
  }

  loadSettings(): void {
    this.api
      .getSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
            this.settings = this.normalizeSettings(settings);
        },
        error: (err) => {
          console.error('Failed to load translation settings', err);
        },
      });
  }

  saveSettings(): void {
    const payload: TranslationSettings = {
        ...this.normalizeSettings(this.settings),
      defaultInstructions: (this.settings.defaultInstructions || '').trim(),
      defaultModel: (this.settings.defaultModel || '').trim() || OPENAI_LUNA_MODEL,
      formPolicy: this.settings.formPolicy || 'auto',
      ctaPolicy: this.settings.ctaPolicy || 'auto',
        syncConnectionId:
            (this.settings.syncConnectionId || this.selectedConnectionId || '').trim() || undefined,
    };

    this.api
      .saveSettings(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
            const merged: TranslationSettings = {
                ...this.settings,
                ...settings,
                autoTranslateLanguages:
                    settings.autoTranslateLanguages ?? this.settings.autoTranslateLanguages ?? [],
            };
            this.settings = this.normalizeSettings(merged);
          this.toastService.showSuccess('Settings saved', 2000);
        },
        error: (err) => {
          console.error('Failed to save translation settings', err);
          this.toastService.showError('Could not save settings', 3000);
        },
      });
  }

  loadPages(reset = true): void {
    if (!this.selectedConnectionId && this.connections.length) {
      this.selectedConnectionId = this.connections[0].id;
    }

    this.pagesLoadError = '';

    if (reset) {
      this.pages = [];
      this.pagesNext = undefined;
    }

    this.pagesLoading = true;
    this.api
      .listPages(this.contentType, {
        connectionId: this.selectedConnectionId || undefined,
        after: reset ? undefined : this.pagesNext || undefined,
        limit: 50,
        groupByMaster: true,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          const items = resp?.items ?? [];
          this.pages = reset ? items : [...this.pages, ...items];
          this.pagesNext = resp?.next ?? null;
          this.pagesLoading = false;
          this.pagesLoadError = '';
        },
        error: (err) => {
          console.error('Failed to load pages', err);
          this.pagesLoading = false;
          this.pagesLoadError = 'Could not load pages. Retry to try again.';
          this.toastService.showError('Could not load pages', 3000);
        },
      });
  }

  loadJobs(): void {
    this.jobsLoading = true;
    this.jobsLoadError = '';
    this.api
      .listJobs(50, 0)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (jobs) => {
          this.jobs = jobs ?? [];
          this.jobsLoading = false;
          this.jobsLoadError = '';
        },
        error: (err) => {
          console.error('Failed to load translation jobs', err);
          this.jobsLoading = false;
          this.jobsLoadError = 'Could not load translation jobs. Retry to try again.';
        },
      });
  }

  translatePage(page: CmsPageSummary): void {
    const target = this.targetLanguage.trim();
    if (!target) {
      this.toastService.showError('Target language is required', 2500);
      return;
    }

    const request: TranslationJobRequest = {
      contentType: this.contentType,
      sourceId: page.id,
      targetLanguage: target,
      sourceLanguage: (page.language || '').trim() || undefined,
      connectionId: this.selectedConnectionId || undefined,
      instructions: this.jobInstructions.trim() || undefined,
    };

    this.api
      .createJob(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (job) => {
          this.jobs = [job, ...this.jobs];
          this.toastService.showSuccess('Translation job queued', 2000);
        },
        error: (err) => {
          console.error('Failed to create translation job', err);
          this.toastService.showError('Could not create job', 3000);
        },
      });
  }

  retranslateVariation(page: CmsPageSummary, variation: CmsLanguageVariation): void {
    const target = (variation.language || '').trim();
    if (!target) {
      this.toastService.showError('Variation language is missing', 2500);
      return;
    }

    const request: TranslationJobRequest = {
      contentType: this.contentType,
      sourceId: page.id,
      targetLanguage: target,
      sourceLanguage: (page.language || '').trim() || undefined,
      connectionId: this.selectedConnectionId || undefined,
      instructions: this.jobInstructions.trim() || undefined,
    };

    this.confirmDialog
      .confirm({
        title: 'Retranslate variation?',
        message: `Retranslate "${page.name || `Page ${page.id}`}" into ${this.formatLanguage(target)}.`,
        info: 'This enqueues a fresh translation job and may overwrite the current Shine draft variation.',
        confirmButtonText: 'Retranslate',
        cancelButtonText: 'Cancel',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.api
          .createJob(request)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (job) => {
              this.jobs = [job, ...this.jobs];
              this.toastService.showSuccess('Retranslation job queued', 2000);
            },
            error: (err) => {
              console.error('Failed to create retranslation job', err);
              this.toastService.showError('Could not create retranslation job', 3000);
            },
          });
      });
  }

    isExternallyEdited(page: CmsPageSummary, variation: CmsLanguageVariation): boolean {
        return (page.outOfSyncVariations || []).some((entry) => entry.id === variation.id);
    }

    runSyncNow(): void {
        if (!this.isSyncEnabled()) {
            this.toastService.showInfo('Enable auto-sync to run it', 2500);
            return;
        }

        this.confirmDialog
            .confirm({
                title: 'Run auto-sync now?',
                message: `Run ${this.describeContentType(this.contentType)} auto-sync now?`,
                info: 'This may queue multiple translation jobs for eligible pages.',
                confirmButtonText: 'Run sync',
                cancelButtonText: 'Cancel',
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) {
                    return;
                }

                this.api
                    .runSync({contentType: this.contentType})
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (result: TranslationSyncResult) => {
                            this.toastService.showSuccess(`Sync run queued ${result.jobsQueued} jobs`, 3000);
                        },
                        error: (err) => {
                            console.error('Failed to run sync', err);
                            this.toastService.showError('Could not run sync', 3000);
                        },
                    });
            });
    }

    runSyncForPage(page: CmsPageSummary): void {
        if (!this.isSyncEnabled()) {
            this.toastService.showInfo('Enable auto-sync to run it', 2500);
            return;
        }

        this.confirmDialog
            .confirm({
                title: 'Run page sync?',
                message: `Run ${this.describeContentType(this.contentType)} sync for "${page.name || `Page ${page.id}`}"?`,
                info: 'This forces a sync check for the selected page and may queue translation jobs immediately.',
                confirmButtonText: 'Run sync',
                cancelButtonText: 'Cancel',
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) {
                    return;
                }

                this.api
                    .runSync({contentType: this.contentType, pageId: page.id, force: true})
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (result: TranslationSyncResult) => {
                            this.toastService.showSuccess(`Sync queued ${result.jobsQueued} jobs`, 3000);
                        },
                        error: (err) => {
                            console.error('Failed to run page sync', err);
                            this.toastService.showError('Could not run sync', 3000);
                        },
                    });
            });
    }

    toggleSync(page: CmsPageSummary): void {
        const config = this.getSyncConfig(this.contentType);
        const mode = config.mode;

        if (mode === 'off') {
            this.toastService.showInfo('Enable auto-sync to manage pages', 2500);
            return;
        }

        if (mode === 'include') {
            const included = new Set(config.includeIds || []);
            if (included.has(page.id)) included.delete(page.id);
            else included.add(page.id);
            this.setSyncConfig(this.contentType, {...config, includeIds: Array.from(included)});
        } else {
            const excluded = new Set(config.excludeIds || []);
            if (excluded.has(page.id)) excluded.delete(page.id);
            else excluded.add(page.id);
            this.setSyncConfig(this.contentType, {...config, excludeIds: Array.from(excluded)});
        }

        this.saveSettings();
    }

    setSyncMode(mode: TranslationSyncMode): void {
        const config = this.getSyncConfig(this.contentType);
        if (config.mode === mode) {
            return;
        }

        const applyModeChange = () => {
            this.setSyncConfig(this.contentType, {...config, mode});
            this.saveSettings();
        };

        if (!this.isBroadeningSyncMode(config.mode, mode)) {
            applyModeChange();
            return;
        }

        this.confirmDialog
            .confirm({
                title: 'Broaden auto-sync scope?',
                message: `Change ${this.describeContentType(this.contentType)} auto-sync from ${this.describeSyncMode(config.mode)} to ${this.describeSyncMode(mode)}?`,
                info: 'This broadens the set of pages eligible for automatic translation sync.',
                confirmButtonText: 'Broaden sync',
                cancelButtonText: 'Cancel',
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) {
                    return;
                }

                applyModeChange();
            });
    }

    syncActionLabel(page: CmsPageSummary): string {
        const config = this.getSyncConfig(this.contentType);
        if (config.mode === 'include') {
            return this.isPageIncluded(page) ? 'Remove sync' : 'Keep in sync';
        }
        if (config.mode === 'exclude') {
            return this.isPageExcluded(page) ? 'Include in sync' : 'Exclude from sync';
        }
        return 'Sync disabled';
    }

    syncStatusLabel(page: CmsPageSummary): string | null {
        const config = this.getSyncConfig(this.contentType);
        if (config.mode === 'include') {
            return this.isPageIncluded(page) ? 'In sync' : null;
        }
        if (config.mode === 'exclude') {
            return this.isPageExcluded(page) ? 'Excluded' : null;
        }
        return null;
    }

    isSyncEnabled(): boolean {
        return this.getSyncConfig(this.contentType).mode !== 'off';
    }

    isPageIncluded(page: CmsPageSummary): boolean {
        const config = this.getSyncConfig(this.contentType);
        return (config.includeIds || []).includes(page.id);
    }

    isPageExcluded(page: CmsPageSummary): boolean {
        const config = this.getSyncConfig(this.contentType);
        return (config.excludeIds || []).includes(page.id);
    }

    getSyncConfig(contentType: CmsContentType): TranslationSyncConfig {
        switch (contentType) {
            case 'LandingPage':
                return this.settings.syncLandingPages ?? {...DEFAULT_SYNC_CONFIG};
            case 'BlogPost':
                return this.settings.syncBlogPosts ?? {...DEFAULT_SYNC_CONFIG};
            case 'SitePage':
            default:
                return this.settings.syncSitePages ?? {...DEFAULT_SYNC_CONFIG};
        }
    }

    filteredAutoLanguages(): LanguageOption[] {
        const query = this.autoLanguageInput.trim().toLowerCase();
        const selected = new Set(
            (this.settings.autoTranslateLanguages ?? []).map((code) => this.normalizeLanguageCode(code)),
        );

        return this.languageOptions.filter((option) => {
            const code = option.code.toLowerCase();
            if (selected.has(code)) return false;
            if (!query) return true;
            const label = option.label.toLowerCase();
            return label.includes(query) || code.includes(query);
        });
    }

    formatAutoLanguage(code: string): string {
        const normalized = this.normalizeLanguageCode(code);
        const match = this.languageOptions.find((option) => option.code.toLowerCase() === normalized);
        return match ? this.displayLanguage(match) : normalized;
    }

  canTranslate(): boolean {
    return !!this.targetLanguage.trim();
  }

  filteredLanguages(): LanguageOption[] {
    const query = this.targetLanguage.trim().toLowerCase();
    if (!query) return this.languageOptions;
    return this.languageOptions.filter((option) => {
      const label = option.label.toLowerCase();
      const code = option.code.toLowerCase();
      return label.includes(query) || code.includes(query);
    });
  }

  displayLanguage(option: LanguageOption): string {
    return `${option.label} (${option.code})`;
  }

  formatLanguage(value?: string | null): string {
    const normalized = (value || '').trim();
    if (!normalized) {
      return '—';
    }

    const match = this.languageOptions.find((option) => option.code.toLowerCase() === normalized.toLowerCase());
    return match ? match.label : normalized;
  }

    addAutoLanguageFromInput(event: MatChipInputEvent): void {
        const value = event.value || '';
        this.addAutoLanguage(value);
        event.chipInput?.clear();
        this.autoLanguageInput = '';
    }

    selectAutoLanguage(code: string): void {
        this.addAutoLanguage(code);
        this.autoLanguageInput = '';
    }

    removeAutoLanguage(code: string): void {
        const normalized = this.normalizeLanguageCode(code);
        const current = (this.settings.autoTranslateLanguages ?? []).map((entry) =>
            this.normalizeLanguageCode(entry),
        );
        const next = current.filter((entry) => entry !== normalized);
        if (next.length === current.length) return;
        this.settings = {...this.settings, autoTranslateLanguages: next};
        this.saveSettings();
    }

    private setSyncConfig(contentType: CmsContentType, config: TranslationSyncConfig): void {
        switch (contentType) {
            case 'LandingPage':
                this.settings = {...this.settings, syncLandingPages: config};
                break;
            case 'BlogPost':
                this.settings = {...this.settings, syncBlogPosts: config};
                break;
            case 'SitePage':
            default:
                this.settings = {...this.settings, syncSitePages: config};
                break;
        }
    }

    private normalizeSettings(settings: TranslationSettings): TranslationSettings {
        const defaultModel = (settings.defaultModel ?? '').trim() || OPENAI_LUNA_MODEL;
        const autoTranslateLanguages = Array.from(
            new Set(
                (settings.autoTranslateLanguages ?? [])
                    .map((entry) => this.normalizeLanguageCode(entry))
                    .filter((entry) => !!entry),
            ),
        );
        return {
            ...settings,
            defaultModel,
            autoTranslateLanguages,
            syncSitePages: settings.syncSitePages ?? {...DEFAULT_SYNC_CONFIG},
            syncLandingPages: settings.syncLandingPages ?? {...DEFAULT_SYNC_CONFIG},
            syncBlogPosts: settings.syncBlogPosts ?? {...DEFAULT_SYNC_CONFIG},
        };
    }

    private addAutoLanguage(raw: string): void {
        const normalized = this.normalizeLanguageCode(raw);
        if (!normalized) return;
        const current = (this.settings.autoTranslateLanguages ?? []).map((entry) =>
            this.normalizeLanguageCode(entry),
        );
        if (current.includes(normalized)) return;
        this.settings = {...this.settings, autoTranslateLanguages: [...current, normalized]};
        this.saveSettings();
    }

    private normalizeLanguageCode(value?: string | null): string {
        return (value || '').trim().toLowerCase().replace('_', '-');
    }

  private isBroadeningSyncMode(currentMode: TranslationSyncMode, nextMode: TranslationSyncMode): boolean {
    return nextMode === 'exclude' && currentMode !== 'exclude';
  }

  private describeContentType(contentType: CmsContentType): string {
    switch (contentType) {
      case 'LandingPage':
        return 'landing page';
      case 'BlogPost':
        return 'blog post';
      case 'SitePage':
      default:
        return 'site page';
    }
  }

  private describeSyncMode(mode: TranslationSyncMode): string {
    switch (mode) {
      case 'include':
        return 'only selected pages';
      case 'exclude':
        return 'all pages except excluded';
      case 'off':
      default:
        return 'off';
    }
  }

  trackPage(_: number, page: CmsPageSummary): number {
    return page.id;
  }

  trackVariation(_: number, variation: CmsLanguageVariation): number {
    return variation.id;
  }

  trackJob(_: number, job: TranslationJob): string {
    return job.id;
  }

  hasVariations(page: CmsPageSummary): boolean {
    return (page.variations?.length ?? 0) > 0;
  }

  variationLabel(variation: CmsLanguageVariation): string {
    const language = this.formatLanguage(variation.language);
    const state = (variation.state || 'draft').toLowerCase();
    return `${language} · ${state}`;
  }
}
